import ViewerFragment, {
  ViewerFragmentCommonProps,
} from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/ViewerFragment";
import ViewerFragmentConfig from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/ViewerFragmentConfig";
import {
  getByPath,
  safeGetByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import styles from "./GroupedViewerFragment.module.css";

export type GroupedViewerFragmentConfigProps = {
  fragments: ViewerFragmentConfig[];
};

type GroupedViewerFragmentProps = {
  configProps: GroupedViewerFragmentConfigProps;
  commonProps: ViewerFragmentCommonProps;
};

function fragmentHasContent(
  config: ViewerFragmentConfig,
  task: ViewerFragmentCommonProps["task"]
): boolean {
  if (config.id === "titled-text-field-viewer-fragment") {
    const text = getTypedValue<string>(getByPath(task, config.props.field));
    return !isLoading(text) && text != null && String(text).length > 0;
  }
  if (config.id === "titled-text-with-badges-viewer-fragment") {
    const title = getTypedValue<string>(
      safeGetByPath(task, config.props.titleField)
    );
    return !isLoading(title) && !!title;
  }
  return true;
}

function GroupedViewerFragment({
  configProps,
  commonProps,
}: GroupedViewerFragmentProps) {
  const { fragments } = configProps;
  // Compute keys from the original (unfiltered) index so a fragment's key
  // stays stable even if an earlier sibling's visibility changes - otherwise
  // every fragment after it would shift to a different filtered index and
  // React would remount them instead of recognizing them as the same node.
  const visibleFragments = fragments
    .map((config, index) => ({ config, index }))
    .filter(({ config }) => fragmentHasContent(config, commonProps.task));

  if (visibleFragments.length === 0) {
    return null;
  }

  return (
    <div className={styles["root"]}>
      {visibleFragments.map(({ config, index }) => (
        <ViewerFragment
          key={`${config.id}-${index}`}
          config={config}
          commonProps={commonProps}
        />
      ))}
    </div>
  );
}

export default GroupedViewerFragment;
